import type { HistoryEntry } from "@fluentry/domain";

export type LocalHistoryOptions = {
  storageKey?: string;
  maxEntries?: number;
  ttlMs?: number;
};

const DEFAULTS: Required<LocalHistoryOptions> = {
  storageKey: "fluentry:history",
  maxEntries: 50,
  ttlMs: 1000 * 60 * 60 * 24 * 7,
};

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function nowMs(): number {
  return Date.now();
}

export function loadLocalHistory(options: LocalHistoryOptions = {}): HistoryEntry[] {
  if (!isBrowser()) return [];

  const { storageKey, ttlMs } = { ...DEFAULTS, ...options };
  const raw = window.localStorage.getItem(storageKey);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as HistoryEntry[];
    const cutoff = nowMs() - ttlMs;
    return parsed.filter((entry) => {
      const createdAt =
        typeof entry.createdAt === "number" ? entry.createdAt : new Date(entry.createdAt).getTime();
      return createdAt >= cutoff;
    });
  } catch {
    return [];
  }
}

export function saveLocalHistory(entries: HistoryEntry[], options: LocalHistoryOptions = {}): void {
  if (!isBrowser()) return;
  const { storageKey, maxEntries } = { ...DEFAULTS, ...options };
  const limited = entries.slice(0, maxEntries);
  window.localStorage.setItem(storageKey, JSON.stringify(limited));
}

export function appendLocalHistory(
  entry: HistoryEntry,
  options: LocalHistoryOptions = {},
): HistoryEntry[] {
  const entries = loadLocalHistory(options);
  const updated = [entry, ...entries].slice(0, { ...DEFAULTS, ...options }.maxEntries);
  saveLocalHistory(updated, options);
  return updated;
}
